import React from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Stack,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Add as AddIcon,
  List as ListIcon,
  AutoStories as AutoStoriesIcon,
  Assessment as AssessmentIcon,
  Forum as ForumIcon,
  Bookmark as BookmarkIcon,
  PlaylistAdd as PlaylistAddIcon,
  Edit as EditIcon,
  ManageSearch as ManageSearchIcon,
  MergeType as MergeTypeIcon,
  Quiz as QuizIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import NotebookAgentAvatar from '../../../../components/Avatar/NotebookAgentAvatar';

export default function ChatSidebar({
  notebookTitle,
  notebookId,
  agentAvatar,
  sidebarCollapsed,
  setSidebarCollapsed,
  sidebarTab,
  setSidebarTab,
  conversations,
  currentConversationId,
  loadingConversations,
  isTeacher,
  navigate,
  formatDate,
  handleSelectConversation,
  handleDeleteConversation,
  handleNewChat,
  handleGenerateDescription,
  handleStartReview,
  reviewLoading,
  setOutlineDialogOpen,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        width: sidebarCollapsed ? 72 : 270,
        minWidth: sidebarCollapsed ? 72 : 270,
        m: 2,
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        zIndex: 1,
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        borderRadius: 2,
        border: '1px solid rgba(255, 255, 255, 0.08)',
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(6px) saturate(120%)',
        WebkitBackdropFilter: 'blur(6px) saturate(120%)',
        transition: 'all 0.3s ease',
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, p: sidebarCollapsed ? 1.5 : 2 }}>
        <Box sx={{ flexShrink: 0, p: sidebarCollapsed ? 1.5 : 2, display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1, position: 'relative' }}>
          {!sidebarCollapsed && (
            <>
              <NotebookAgentAvatar agentAvatar={agentAvatar} notebookId={notebookId} size={72} />
              <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1, color: 'text.primary', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }} noWrap>
                {notebookTitle || '笔记本'} Agent
              </Typography>
            </>
          )}
          <IconButton
            size="small"
            onClick={() => setSidebarCollapsed((v) => !v)}
            title={sidebarCollapsed ? '展开侧边栏' : '收起侧边栏'}
            sx={{
              color: 'text.secondary',
              width: 28,
              height: 28,
              position: sidebarCollapsed ? 'relative' : 'absolute',
              top: sidebarCollapsed ? 0 : 8,
              left: sidebarCollapsed ? 0 : 8,
              bgcolor: sidebarCollapsed ? 'transparent' : 'background.paper',
              boxShadow: sidebarCollapsed ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
              '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
            }}
          >
            {sidebarCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
          </IconButton>
        </Box>
        {sidebarCollapsed && (
          <>
            <Divider />
            <Box sx={{ flexShrink: 0, p: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <NotebookAgentAvatar agentAvatar={agentAvatar} notebookId={notebookId} size={40} />
              <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary', maxWidth: 56, overflow: 'hidden', textOverflow: 'ellipsis' }} noWrap>
                {notebookTitle || '笔记本'} Agent
              </Typography>
            </Box>
          </>
        )}
        {!sidebarCollapsed && (
          <>
            <Tabs
              value={sidebarTab === 'history' ? 0 : 1}
              onChange={(_, v) => setSidebarTab(v === 0 ? 'history' : 'skills')}
              sx={{
                minHeight: 36,
                mb: 1,
                '& .MuiTabs-indicator': { bgcolor: 'primary.main' },
                '& .MuiTab-root': { minHeight: 36, py: 0, fontSize: '0.8125rem', textTransform: 'none' },
              }}
            >
              <Tab label="历史消息" />
              <Tab label="Skills" />
            </Tabs>
            <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
              {sidebarTab === 'history' ? (
                loadingConversations ? (
                  <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={24} sx={{ color: 'text.secondary' }} />
                  </Box>
                ) : (
                  <List dense disablePadding>
                    {conversations.map((c) => (
                      <Box
                        key={c.id}
                        sx={{
                          position: 'relative',
                          mb: 1,
                          '&:hover .conv-delete-btn': { opacity: 1 },
                        }}
                      >
                        <ListItemButton
                          selected={currentConversationId === c.id}
                          onClick={() => handleSelectConversation(c.id)}
                          sx={{ borderRadius: 1, pr: 4 }}
                        >
                          <ListItemText
                            primary={c.questionTitle || c.preview || '对话'}
                            secondary={formatDate(c.lastMessageAt)}
                            primaryTypographyProps={{ noWrap: true, fontSize: '0.875rem' }}
                            secondaryTypographyProps={{ fontSize: '0.75rem' }}
                          />
                        </ListItemButton>
                        <IconButton
                          className="conv-delete-btn"
                          size="small"
                          onClick={(e) => handleDeleteConversation(e, c.id)}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            color: 'text.secondary',
                            '&:hover': { color: 'error.main', bgcolor: 'error.light' },
                          }}
                          aria-label="删除对话"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </List>
                )
              ) : (
                <List dense disablePadding>
                  {[
                    { label: '获取大纲', icon: <ListIcon sx={{ fontSize: 18 }} />, onClick: () => setOutlineDialogOpen(true), glass: 'rgba(102, 126, 234, 0.25)' },
                    ...(!isTeacher ? [
                      { label: '开始复习', icon: <AutoStoriesIcon sx={{ fontSize: 18 }} />, onClick: handleStartReview, glass: 'rgba(33, 150, 243, 0.25)' },
                      { label: '分析学习状态', icon: <AssessmentIcon sx={{ fontSize: 18 }} />, onClick: () => navigate(`/notebook/${notebookId}/learning-state`), glass: 'rgba(233, 71, 245, 0.25)' },
                      { label: '发帖', icon: <ForumIcon sx={{ fontSize: 18 }} />, onClick: () => navigate('/forum'), glass: 'rgba(47, 75, 130, 0.25)' },
                      { label: '查看本笔记错题本', icon: <BookmarkIcon sx={{ fontSize: 18 }} />, onClick: () => navigate(`/bookmarks?notebook=${encodeURIComponent(notebookId)}`), glass: 'rgba(76, 175, 80, 0.25)' },
                      { label: '生成更多题目', icon: <PlaylistAddIcon sx={{ fontSize: 18 }} />, onClick: () => navigate(`/questions/notebook/${notebookId}`), glass: 'rgba(255, 152, 0, 0.25)' },
                    ] : [
                      { label: '生成描述', icon: <DescriptionIcon sx={{ fontSize: 18 }} />, onClick: handleGenerateDescription, glass: 'rgba(156, 39, 176, 0.25)' },
                      { label: '添加一道题目', icon: <AddIcon sx={{ fontSize: 18 }} />, onClick: () => navigate(`/questions/notebook/${notebookId}`), glass: 'rgba(102, 126, 234, 0.25)' },
                      { label: '添加多道题目', icon: <PlaylistAddIcon sx={{ fontSize: 18 }} />, onClick: () => navigate(`/notebook/${notebookId}/add-exercises`), glass: 'rgba(118, 75, 162, 0.25)' },
                      { label: '生成题目', icon: <QuizIcon sx={{ fontSize: 18 }} />, onClick: () => navigate(`/questions/notebook/${notebookId}`), glass: 'rgba(233, 71, 245, 0.25)' },
                      { label: '编辑笔记', icon: <EditIcon sx={{ fontSize: 18 }} />, onClick: () => navigate(`/notebook/${notebookId}`), glass: 'rgba(47, 75, 130, 0.25)' },
                      { label: '管理题目', icon: <ManageSearchIcon sx={{ fontSize: 18 }} />, onClick: () => navigate(`/questions/notebook/${notebookId}`), glass: 'rgba(76, 175, 80, 0.25)' },
                      { label: '题目去重', icon: <MergeTypeIcon sx={{ fontSize: 18 }} />, onClick: () => navigate(`/questions/notebook/${notebookId}`), glass: 'rgba(255, 152, 0, 0.25)' },
                      { label: '班级情况', icon: <AssessmentIcon sx={{ fontSize: 18 }} />, onClick: () => navigate('/dashboard'), glass: 'rgba(244, 67, 54, 0.25)' },
                    ]),
                  ].map((item) => (
                    <ListItemButton
                      key={item.label}
                      onClick={item.onClick}
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        backgroundColor: item.glass,
                        backdropFilter: 'blur(8px) saturate(150%)',
                        WebkitBackdropFilter: 'blur(8px) saturate(150%)',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        '&:hover': { backgroundColor: item.glass.replace('0.25', '0.35'), borderColor: 'rgba(255, 255, 255, 0.25)' },
                      }}
                    >
                      <Box component="span" sx={{ mr: 1, color: 'inherit', display: 'flex', alignItems: 'center' }}>{item.icon}</Box>
                      <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
            <Stack spacing={1} sx={{ mt: 1, flexShrink: 0 }}>
              <Button fullWidth variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleNewChat}>
                新对话
              </Button>
              {!isTeacher && (
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={reviewLoading ? <CircularProgress size={18} color="inherit" /> : <AutoStoriesIcon />}
                  onClick={handleStartReview}
                  disabled={reviewLoading}
                >
                  {reviewLoading ? 'AI 选题中…' : '开始复习'}
                </Button>
              )}
            </Stack>
          </>
        )}
      </Box>
    </Paper>
  );
}
